import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Button,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { fieldSx, sectionInnerSx } from "./sectionStyles";
import type { ReactNode } from "react";

import { bannerTooltipSlotProps } from "@/components/bannerTooltip";

export interface BroadcastConfirmSectionProps {
  confirmValue: string;
  onConfirmChange: (value: string) => void;
  confirmError?: string;
  onSubmit: () => void;
  /** Disable the whole section (input included), e.g. campaign broadcasts
   * where sending happens by scheduling the campaign, not from the composer. */
  disabled?: boolean;
  submitDisabled: boolean;
  submitBusy?: boolean;
  submitLabel?: string;
  /** Tooltip shown on the submit button when it is disabled. */
  submitDisabledReason?: string;
  /** Show a lock icon on the submit button (when the action is gated/not allowed). */
  submitLocked?: boolean;
  /**
   * Rich tooltip content shown while `submitLocked` (e.g. the SMS-not-enabled
   * banner). Rendered as a bare surface so the node itself is the popup; takes
   * precedence over `submitDisabledReason`.
   */
  lockedTooltip?: ReactNode;
}

/** "Type CONFIRM to complete" gate + submit button. Controlled. */
export function BroadcastConfirmSection({
  confirmValue,
  onConfirmChange,
  confirmError,
  onSubmit,
  disabled,
  submitDisabled,
  submitBusy,
  submitLabel = "Complete",
  submitDisabledReason,
  submitLocked,
  lockedTooltip,
}: BroadcastConfirmSectionProps) {
  const showLockedBanner = Boolean(submitLocked && lockedTooltip);
  return (
    <Stack
      gap={2}
      sx={{
        backgroundColor: (theme) =>
          theme.palette.background["paper-elevation-0"],
        borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
        p: 6,
      }}
    >
      <Typography variant="subtitle1">Confirm broadcast send</Typography>
      <Stack gap={2} sx={sectionInnerSx}>
        <Typography variant="body2" color="text.secondary">
          Type &apos;CONFIRM&apos; to complete
        </Typography>
        {/* Same reason as the CTA, on the whole disabled input (FOG-2496);
            the span wrapper hovers where a disabled input can't. */}
        <Tooltip
          title={(disabled && submitDisabledReason) || ""}
          arrow
          describeChild
        >
          <Box component="span" sx={{ display: "block" }}>
            <TextField
              value={confirmValue}
              onChange={(e) => onConfirmChange(e.target.value)}
              fullWidth
              placeholder="CONFIRM"
              disabled={disabled}
              inputProps={{ "aria-label": "Confirm broadcast send" }}
              error={Boolean(confirmError)}
              helperText={confirmError || undefined}
              sx={fieldSx}
            />
          </Box>
        </Tooltip>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <Tooltip
            title={
              showLockedBanner
                ? lockedTooltip
                : (disabled || submitDisabled) && submitDisabledReason
                  ? submitDisabledReason
                  : ""
            }
            disableHoverListener={
              showLockedBanner
                ? false
                : !(disabled || submitDisabled) || !submitDisabledReason
            }
            arrow={!showLockedBanner}
            slotProps={showLockedBanner ? bannerTooltipSlotProps : undefined}
          >
            <span>
              <Button
                type="button"
                variant="contained"
                color="primary"
                disabled={disabled || submitDisabled || submitBusy}
                startIcon={
                  submitLocked ? <LockIcon fontSize="small" /> : undefined
                }
                onClick={() => {
                  if (!submitBusy) onSubmit();
                }}
                sx={{
                  borderRadius: "9999px",
                  textTransform: "uppercase",
                  px: 6,
                  py: 3,
                }}
              >
                {submitLabel}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Stack>
    </Stack>
  );
}
